export type FulfillmentOrder = {
  id: string; name: string; createdAt: string; fulfillmentStatus: string;
  customerName: string | null; recipient: string | null; company: string | null;
  address: string[]; country: string | null; notes: string | null; linesTruncated: boolean;
  lines: Array<{ id: string; name: string; sku: string | null; quantity: number; unfulfilledQuantity: number; requiresShipping: boolean }>;
};
export type FulfillmentFeed = {
  connected: boolean; shopDomain?: string; fetchedAt?: string; truncated?: boolean; orders: FulfillmentOrder[];
};
export const remainingUnits = (order: FulfillmentOrder) => order.lines.reduce((sum, line) => sum + line.unfulfilledQuantity, 0);
export const fulfillmentLabel = (status: string) => ({
  UNFULFILLED: 'Unfulfilled', PARTIALLY_FULFILLED: 'Partially fulfilled', ON_HOLD: 'On hold',
  IN_PROGRESS: 'In progress', SCHEDULED: 'Scheduled', REQUEST_DECLINED: 'Request declined',
  RESTOCKED: 'Restocked', PENDING_FULFILLMENT: 'Pending fulfillment', OPEN: 'Open', FULFILLED: 'Fulfilled',
}[status] || status?.toLowerCase().replace(/_/g, ' ') || 'Status unavailable');

export function shopifyOrderUrl(domain: string | undefined, id: string) {
  const shop = /^([a-z0-9][a-z0-9-]*)\.myshopify\.com$/i.exec(domain || '');
  const order = /^gid:\/\/shopify\/Order\/(\d+)$/.exec(id);
  return shop && order ? `https://admin.shopify.com/store/${shop[1]}/orders/${order[1]}` : null;
}
export function filterFulfillmentOrders(orders: FulfillmentOrder[], search: string, status = '', country = '') {
  const needle = search.trim().toLowerCase();
  return orders.filter(order => (!status || order.fulfillmentStatus === status)
    && (!country || (order.country || 'Not supplied') === country)
    && [order.name, order.customerName, order.recipient, order.company, ...order.address,
      ...order.lines.flatMap(line => [line.name, line.sku])].join(' ').toLowerCase().includes(needle));
}
export function fulfillmentProducts(orders: FulfillmentOrder[]) {
  const products = new Map<string, { name: string; sku: string | null; units: number; orders: Set<string> }>();
  for (const order of orders) for (const line of order.lines) {
    if (line.unfulfilledQuantity <= 0) continue;
    // An absent SKU is never merged with a different named product.
    const key = JSON.stringify([line.sku, line.name]);
    const product = products.get(key) || { name: line.name, sku: line.sku, units: 0, orders: new Set<string>() };
    product.units += line.unfulfilledQuantity;
    product.orders.add(order.id);
    products.set(key, product);
  }
  return [...products.values()].map(product => ({ ...product, orders: product.orders.size }))
    .sort((a, b) => b.units - a.units || a.name.localeCompare(b.name));
}
export function fulfillmentCsv(orders: FulfillmentOrder[]) {
  const rows: Array<Array<string | number>> = [['Order', 'Order date', 'Customer', 'Recipient', 'Company', 'Shipping address', 'Country', 'Shopify fulfillment status', 'Product', 'SKU', 'Ordered (net of refunds)', 'Unfulfilled units', 'Item coverage']];
  for (const order of orders) for (const line of order.lines) rows.push([
    order.name, order.createdAt, order.customerName || '', order.recipient || '', order.company || '', order.address.join(', '),
    order.country || '', fulfillmentLabel(order.fulfillmentStatus), line.name, line.sku || '', line.quantity, line.unfulfilledQuantity,
    order.linesTruncated ? 'Partial — review Shopify' : 'Complete',
  ]);
  // Quote all cells and neutralize formula prefixes in customer-supplied text.
  return rows.map(row => row.map(value => {
    const text = String(value);
    const safe = /^[\s]*[=+@-]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  }).join(',')).join('\r\n');
}
